import { useTranslation } from "react-i18next";
import { Combobox, type ComboboxOption } from "@/shared/ui/Combobox";
import type { ColorOption } from "../types";

// Sentinel id for the "clear selection" row. Distinct from "" (Combobox's none-selected value) so the
// placeholder stays muted while still offering a way back to no colour once one is picked.
const CLEAR = "__clear__";

/**
 * Colour picker — brand-themed searchable Combobox (Radix Popover + cmdk; was a Radix Select).
 * Colour is stored as a name string (additional_data['color']) — not an id, so option ids ARE the name.
 *
 * Props:
 *   value       — currently selected colour name (empty string = none selected).
 *   options     — list from useColorOptions().
 *   onChange    — called with the new name string when the user picks ("" = cleared).
 *   isLoading   — disables the trigger while fetching.
 *   isMissing   — paints the amber warning border when the field is required but empty.
 *   id          — forwarded to the trigger (for <label htmlFor>).
 *   placeholder — text shown for the empty/unselected option.
 *
 * A legacy/unknown value not in `options` is rendered as its own row so it isn't silently dropped.
 */
export function ColorSelect({
  value,
  options,
  onChange,
  isLoading,
  isMissing,
  id,
  placeholder = "—",
}: {
  value: string;
  options: ColorOption[];
  onChange: (name: string) => void;
  isLoading?: boolean;
  isMissing?: boolean;
  id?: string;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const isUnknown = value !== "" && !options.some((o) => o.name === value);

  const comboOptions: ComboboxOption[] = [
    // Clear row — always present so a chosen colour can be reset to none.
    { id: CLEAR, label: placeholder },
    // Legacy/unknown value not in the current options list — keep it visible.
    ...(isUnknown ? [{ id: value, label: value }] : []),
    ...options.map((o) => ({ id: o.name, label: o.name })),
  ];

  return (
    <Combobox
      id={id}
      options={comboOptions}
      value={value}
      onSelect={(picked) => onChange(picked === CLEAR ? "" : picked)}
      placeholder={placeholder}
      searchPlaceholder={t("import.colorSelect.search")}
      isLoading={isLoading}
      warning={isMissing}
    />
  );
}
